import { createSlice } from '@reduxjs/toolkit';
import {
  createNewTourNotes,
  createTour,
  emptyMetaData,
  emptypriceListing,
  fetchInboxMetaData,
  fetchLeadApartmentUnits,
  fetchMetaData,
  fetchPriceListings,
  fetchPropertyByApartmentIdOffPlatform,
  fetchUnitPricingMatrix,
  fetchTimeSlots,
  fetchTourConfirmation,
  getNotesByTourId,
  updateStage,
  deleteTour
} from '../Actions/InboxV1';

// Units have no per-unit `_id`. Synthesize a stable row id from
// the physical-unit key (unitName + siteId) so the generic tables that key/select
// on `_id` keep working, including multi-site communities where unitName repeats.
const unitRowId = u => (u?.siteId ? `${u.siteId}:${u.unitName}` : u?.unitName);
const normalizeUnits = payload => {
  const units = payload?.data?.units;
  return Array.isArray(units)
    ? units.map(u => ({ ...u, _id: unitRowId(u) }))
    : [];
};

const initialState = {
  slotsLoading: false,
  slots: [],
  dates: [],
  inboxMetaData: {},
  isTourCreated: false,
  messageDelivered: {},
  messageSent: false,
  tourId: null,
  confirmedTour: false,
  tourConfirmationData: {
    confirmed: false,
    errorType: ''
  },
  tourConfirmationFetchFailed: false,

  inboxMessages: [],
  messagesLoading: false,
  loadMoreMessages: true,
  leadMessageLoading: false,
  leadMessage: [],
  propertyData: [],
  propertyDataLoading: false,
  inboxMetaLoading: false,
  newNoteInserted: false,
  newNoteInsertionInProgress: false,
  newTourNoteInserted: false,
  newTourNoteInsertionInProgress: false,
  commentsData: [],
  priceListings: [],
  priceListingsLoading: false,
  isPriceListingLoading: false,
  priceListingList: [],
  newCommentInserted: 0,
  updatedLeadId: false,
  isLeadsEmpty: false,
  toastMessage: {},
  priceListingError: false,
  privateInfo: {},
  isPrivateInfoLoading: false,
  allPropertyData: [],
  offPlatformPropertyByApi: [],
  leadApartmentUnits: [],
  unitMatrix: null,
  isUnitMatrixLoading: false,
  unitMatrixError: false,
  isStageUpdated: false,
  slotsMessage: '',
  slotsErrorMessage: '',
  deleteTourPending: false
};

const InboxSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    chatArchived: (state, action) => {
      const leadDataIndex = action?.payload?.guestCards?.findIndex(
        data => data?._id?._id === action?.payload?.id
      );
      if (leadDataIndex > -1) {
        state.updatedLeadId =
          action.payload?.guestCards?.[leadDataIndex + 1]?._id?._id || false;
      }
      const inboxMessages = [
        ...(action.payload.guestCards.map((data, index) => {
          return data._id._id === action.payload.id
            ? { ...data, _id: { ...data._id, isArchived: true } }
            : data;
        }) || [])
      ];
      action?.payload?.setGuestCards([...inboxMessages]);
    },
    setDefaultPropertyData: (state, action) => {
      state.propertyData = action?.payload?.data ? action?.payload : {};
    },
    setMessagesEmpty: (state, action) => {
      state.slotsMessage = '';
      state.slotsErrorMessage = '';
    }
  },
  extraReducers: {
    [createNewTourNotes.pending]: state => {
      state.newNoteInsertionInProgress = true;
    },
    [createNewTourNotes.fulfilled]: state => {
      state.newNoteInsertionInProgress = false;
    },
    [fetchTimeSlots.pending]: state => {
      state.slotsLoading = true;
    },
    [fetchTimeSlots.fulfilled]: (state, action) => {
      state.slotsLoading = false;

      state.dates = action?.payload?.data?.data?.dates;
      state.slots = action.payload?.data?.data?.slots;
      state.slotsMessage = action.payload?.data?.message;
    },
    [fetchTimeSlots.rejected]: (state, action) => {
      state.slotsLoading = false;
      state.slotsErrorMessage = action?.payload?.data?.message;
    },
    [deleteTour.fulfilled]: (state, action) => {
      state.slotsMessage = action.payload?.data?.message;
      state.deleteTourPending = false;
    },
    [deleteTour.pending]: (state, action) => {
      state.deleteTourPending = true;
    },
    [deleteTour.rejected]: (state, action) => {
      state.slotsErrorMessage = action?.payload?.data?.message;
      state.deleteTourPending = false;
    },
    [getNotesByTourId.pending]: state => {
      state.notesData = true;
    },
    [getNotesByTourId.fulfilled]: (state, action) => {
      state.notesData = false;
      state.Notes = action?.payload?.data?.data;
    },
    [getNotesByTourId.rejected]: (state, action) => {
      state.notesData = false;
    },
    [emptyMetaData.fulfilled]: (state, action) => {
      state.inboxMetaData = {};
      state.leadMessage = [];
      state.isLeadsEmpty = false;
      state.loadMoreMessages = true;

      // state.leadMessageLoading = true;
    },
    [emptypriceListing.fulfilled]: (state, action) => {
      state.priceListingList = [];
    },
    [fetchInboxMetaData.pending]: state => {
      state.inboxMetaLoading = true;
    },
    [fetchInboxMetaData.fulfilled]: (state, action) => {
      state.inboxMetaLoading = false;
      state.isNewLeadData = false;
      state.inboxMetaData = action?.payload?.data?.data;
    },
    [fetchInboxMetaData.rejected]: (state, action) => {
      state.inboxMetaLoading = false;
    },
    [createTour.pending]: state => {
      state.isTourCreated = false;
    },
    [createTour.fulfilled]: (state, action) => {
      state.isTourCreated = true;
      state.tourId = action?.payload?.data?.data?._id;
      state.slotsMessage = action?.payload?.data?.message;
      // Reset the failure flag so a fresh booking can resume polling.
      state.tourConfirmationFetchFailed = false;
    },
    [createTour.rejected]: (state, action) => {
      state.isTourCreated = false;
      state.slotsErrorMessage = action?.payload?.data?.message;
    },
    [fetchTourConfirmation.pending]: state => {
      state.confirmedTour = true;
    },
    [fetchTourConfirmation.fulfilled]: (state, action) => {
      state.confirmedTour = false;
      state.tourConfirmationData = action?.payload?.data?.data;
      state.tourConfirmationFetchFailed = false;
    },
    [fetchTourConfirmation.rejected]: (state, action) => {
      state.confirmedTour = false;
      state.tourConfirmationFetchFailed = true;
    },

    [fetchPropertyByApartmentIdOffPlatform.fulfilled]: (state, action) => {
      state.offPlatformPropertyByApi = normalizeUnits(action?.payload);
    },
    [fetchLeadApartmentUnits.fulfilled]: (state, action) => {
      state.leadApartmentUnits = normalizeUnits(action?.payload);
    },
    [fetchUnitPricingMatrix.pending]: state => {
      state.isUnitMatrixLoading = true;
      state.unitMatrixError = false;
    },
    [fetchUnitPricingMatrix.fulfilled]: (state, action) => {
      state.isUnitMatrixLoading = false;
      state.unitMatrix = action?.payload?.data?.unit ?? null;
      state.unitMatrixError = false;
    },
    [fetchUnitPricingMatrix.rejected]: state => {
      state.isUnitMatrixLoading = false;
      state.unitMatrix = null;
      state.unitMatrixError = true;
    },
    [fetchMetaData.pending]: state => {
      state.inboxMetaLoading = true;
    },
    [fetchMetaData.fulfilled]: (state, action) => {
      state.inboxMetaLoading = false;
      state.isNewLeadData = false;
      state.toastMessage = {};
      state.inboxMetaData = { ...action?.payload?.data?.data };
    },
    [updateStage.pending]: state => {
      state.inboxMetaLoading = true;
      state.isStageUpdated = true;
    },
    [fetchPriceListings.pending]: state => {
      state.isPriceListingLoading = true;
      state.priceListingError = false;
    },
    [fetchPriceListings.fulfilled]: (state, action) => {
      state.isPriceListingLoading = false;
      state.priceListingList = normalizeUnits(action?.payload);
      state.priceListingError = false;
    },
    [fetchPriceListings.rejected]: (state, action) => {
      state.isPriceListingLoading = false;
      state.error = action.payload;
      state.priceListingError = true;
    },
    [updateStage.fulfilled]: (state, action) => {
      state.inboxMetaLoading = false;
      state.isStageUpdated = false;
    },
    // Without this the loader never clears and isStageUpdated stays true, so the
    // effect watching it never refetches the lead and the header keeps showing
    // the old stage after a failed update (ZUM-3588 / ZUM-3595).
    [updateStage.rejected]: (state, action) => {
      state.inboxMetaLoading = false;
      state.isStageUpdated = false;
      state.error = action.payload;
    }
  }
});
export const { chatArchived, setMessagesEmpty } = InboxSlice.actions;

export default InboxSlice;
